import { useMemo, useRef } from 'react';
import { useTranslations } from 'next-intl';
import {
  Button,
  SettingsEmptyState,
  TestingInstructionsPanel,
  type TestingInstructionsPanelHandle,
} from '@terra/ui-design-system';
import { useSettingsTabHeaderActions } from './settingsTabHeaderContext';
import { useApplicationTestingInstructionsTarget } from './testing-controls/useApplicationTestingInstructionsTarget';

type TestingInstructionsTabProps = {
  isReadOnly?: boolean;
};

export function TestingInstructionsTab({
  isReadOnly,
}: TestingInstructionsTabProps) {
  const t = useTranslations('settings.instructions');
  const tTestingControls = useTranslations('designSystem.testingControls');
  const panelRef = useRef<TestingInstructionsPanelHandle>(null);
  const target = useApplicationTestingInstructionsTarget();
  const canManage = !isReadOnly;
  const headerActions = useMemo(
    () =>
      canManage ? (
        <Button
          size="xs"
          variant="outline"
          flexShrink={0}
          onClick={() => panelRef.current?.openCreateDialog()}
        >
          {tTestingControls('addPrefix', {
            prefix: tTestingControls('tabs.testingInstructions'),
          })}
        </Button>
      ) : null,
    [canManage, tTestingControls],
  );

  useSettingsTabHeaderActions(target ? headerActions : null);

  if (!target) {
    return <SettingsEmptyState>{t('noTargetDescription')}</SettingsEmptyState>;
  }

  return (
    <TestingInstructionsPanel
      ref={panelRef}
      target={target}
      canManage={canManage}
    />
  );
}
